import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { _find, getPropTranslation } from './form-manager.utils';
import {
  getDatasourceFields as getDatasourceFieldsApi,
  getEntityCatalog,
  getExternalDatasources,
  getFormListByEntityKey,
} from '../../api/forms';
import {
  ALL_SUPPORTED_UPDATE_ACTIONS_TYPES,
  DATASOURCE_TYPE_ENUM,
  DATASOURCE_TYPE_EXTERNALDATASOURCE,
  DATASOURCE_TYPE_FORMDATA,
  DATASOURCE_TYPE_LISTDATA,
  DATASOURCE_TYPE_PREDEFINED,
  DATASOURCE_TYPE_SMARTSOLVEDATA,
  ICON_DEFAULT_COLOR,
  ICON_DEFAULT_SIZE,
  SQL_DICTIONARY,
} from '../constants/editor.constants';
import { ALL_CONTROLS, SS_CONTROL_TYPE } from '../constants/form-manager.constants';

export const normalizeConditionsRows = (entityDefinition, translations, canBeColumn) => (row) => {
  let LeftValue = row.LeftValue;
  let RightValue = row.RightValue;
  if (row.LeftType === 'column' && !canBeColumn) {
    LeftValue = columnValueToFormula(entityDefinition, translations, row.LeftValue);
  } else if (row.LeftType === 'text') {
    LeftValue = columnTextToFormula(row.LeftValue);
  }
  if (row.RightType === 'column') {
    RightValue = columnValueToFormula(entityDefinition, translations, row.RightValue);
  } else if (row.RightType === 'text') {
    RightValue = columnTextToFormula(row.RightValue);
  }

  return {
    ...row,
    OpenGroup: row.OpenGroup ? '(' : '',
    CloseGroup: row.CloseGroup ? ')' : '',
    LeftValue,
    RightValue,
  };
};

const getControlIdForNewElement = (selectedType) =>
  selectedType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX ? `ssAdvancedTextBox_${uuidv4()}` : uuidv4();

export const changeDataSource = ({
  dataSourceObject,
  property,
  propertyValue,
  setDatasourceObject,
}) => {
  _.set(dataSourceObject, `dataSourceProperties.${property}`, propertyValue);
  if (property === 'dataSourceKey') {
    _.set(dataSourceObject, 'dataSourceKey', propertyValue);
  }
  setDatasourceObject(dataSourceObject);
};

const normalizeData = (object) => {
  if (object === undefined || object === null) {
    return object;
  }
  const tempStr = JSON.stringify(object);
  const regexTrue = /"true"/g;
  const regexFalse = /"false"/g;
  return JSON.parse(tempStr.replace(regexTrue, true).replace(regexFalse, false));
};

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object which represent the diff
 */
export const difference = (object, base) => {
  function changes(object, base) {
    return _.transform(object, function(result, value, key) {
      if (!_.isEqual(normalizeData(value), normalizeData(base[key]))) {
        result[key] =
          _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  }

  return changes(object, base);
};

const getFilteredDataWithFields = (node) => {
  if (!node.childNodes?.length) {
    return node;
  }

  const filteredChildNodes = node.childNodes
    .map(getFilteredDataWithFields)
    .filter((childNode) => !childNode.childNodes || childNode.childNodes.length);

  return {
    ...node,
    childNodes: filteredChildNodes.length ? filteredChildNodes : [],
  };
};

const sortDataFields = (node) => {
  if (!node.childNodes?.length) {
    return node;
  }

  return {
    ...node,
    childNodes: _.sortBy(node.childNodes, [
      (childNode) => childNode.nodeId !== `${node.nodeId}-fields`,
      'label',
      (childNode) => childNode.EmployeeID !== 'Field',
      (childNode) => !childNode.label,
    ]),
  };
};

export const renderNodeTree = (entityDefinition, entityTranslations, searchText = '') => {
  const renderTree = (entityDefinition, currentNodeStructure) => {
    const keys = Object.keys(entityDefinition);
    if (keys.length === 1) {
      const root = {
        nodeId: keys[0],
        label: `${entityTranslations[keys[0]]} (${keys[0]})`,
        childNodes: [],
        value: keys[0],
      };
      return renderTree(entityDefinition[keys[0]], root);
    }
    const fieldsDef = {
      nodeId: `${currentNodeStructure.nodeId}-fields`,
      label: 'Fields',
      childNodes: [],
      value: currentNodeStructure.nodeId,
    };

    for (const [ckey, cCurrentValue] of Object.entries(entityDefinition.columns)) {
      if (ckey.includes('WARNINGS') || ckey.includes('ERRORS')) {
        continue;
      }
      const translation = entityTranslations[cCurrentValue.properties.Caption];
      const tempChildNode = {
        nodeId: `${currentNodeStructure.nodeId}-${ckey}`,
        label: `${translation} (${ckey})`,
        value: `{|${translation}|${currentNodeStructure.nodeId}.${ckey}|}`,
        valueCaption: `${currentNodeStructure.nodeId}.${ckey}`,
      };

      fieldsDef.childNodes = [...fieldsDef.childNodes, tempChildNode].sort((a, b) =>
        a.label === 'Fields'
          ? -1
          : a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );
    }

    currentNodeStructure.childNodes = [...currentNodeStructure.childNodes, fieldsDef];

    keys
      .filter((key) => key !== 'columns' && key !== 'properties')
      .forEach((key) => {
        const tempTreeNode = {
          nodeId: key,
          label: `${
            entityTranslations[key.replace(`${currentNodeStructure.nodeId}-`, '')]
          } (${key})`,
          value: key,
          childNodes: [],
        };

        currentNodeStructure.childNodes = [
          ...currentNodeStructure.childNodes,
          renderTree(entityDefinition[key], tempTreeNode, key),
        ];
      });

    fieldsDef.childNodes =
      searchText && fieldsDef.label === 'Fields'
        ? fieldsDef.childNodes.filter((field) =>
            field.label.toLowerCase().includes(searchText.trim().toLowerCase())
          )
        : fieldsDef.childNodes;

    return currentNodeStructure;
  };

  const data = renderTree(entityDefinition);

  const filteredData = searchText ? getFilteredDataWithFields(data) : data;

  return sortDataFields(filteredData);
};

export const getExpandedNodeIds = (data = []) =>
  data.reduce((nodeIds, node) => {
    return node.childNodes?.length
      ? nodeIds.concat(node.nodeId, ...getExpandedNodeIds(node.childNodes))
      : nodeIds;
  }, []);

export const getEntityFields = (entityDefinition, entityTranslations, target) => {
  const dataTree = [renderNodeTree(entityDefinition, entityTranslations)];
  const node = _find(dataTree, 'nodeId', target);
  const fields = node.childNodes.find(({ nodeId }) => nodeId.includes('fields'));

  return fields.childNodes.map(({ valueCaption, label }) => ({
    label,
    value: valueCaption,
  }));
};

export function preProcessRules(formModel) {
  let rules = [];

  function flatten(arr) {
    return arr.reduce((acc, d) => [...acc, ...d], []);
  }

  function getActionsFromRule(rule) {
    return rule.actions.map((action) => ({
      ...rule,
      ...action,
      displaySourceFilter: action.sourceFilter && JSON.stringify(action.sourceFilter),
      displayTargetFilter: action.targetFilter && JSON.stringify(action.targetFilter),
    }));
  }

  Object.keys(formModel).forEach((controlId) => {
    const controlData = formModel[controlId];
    const { entityName, fieldName, controlType, update, interaction } = controlData;
    const controlUpdateRulesTemp = update?.rules ?? [];
    const controlInteractionRulesTemp = interaction?.rules ?? [];

    const controlRules = [...controlUpdateRulesTemp, ...controlInteractionRulesTemp];

    const controlName =
      entityName && fieldName ? `${controlData.entityName}.${controlData.fieldName}` : controlType;
    rules = [
      ...rules,
      ...controlRules.map((rule) => ({
        ...rule,
        scope: controlType === 'ssForm' ? 'form' : 'control',
        conditions: JSON.stringify(rule.Conditions),
        controlId,
        controlName,
      })),
    ];
  });
  const actions = rules.map(getActionsFromRule);

  return flatten(actions).filter((action) =>
    ALL_SUPPORTED_UPDATE_ACTIONS_TYPES.includes(action.type)
  );
}

export const preProcessActions = (rules) => {
  let actions = [];

  function getActionsFromRule(rule) {
    rule.actions.forEach((item) => {
      actions = [...actions, item];
    });
  }

  rules.map(getActionsFromRule);
  return actions;
};

export const findParentByType = (formModel, controlId, formInfo, type) => {
  const panelNode = Object.keys(formModel).filter(
    (key) => formModel[key].childrenNodes && formModel[key].childrenNodes.includes(controlId)
  );
  const findParent = (arr) => {
    if (arr && arr.parent && arr.controlType !== type) {
      return findParent(formModel[arr.parent]);
    } else {
      return arr && arr.formName && arr.formName !== '' ? arr.formName : formInfo.formKey;
    }
  };
  return findParent(formModel[panelNode[0]]);
};

export const getLastModifiedDateContainer = (formModel, containerFormName, formInfo) => {
  const currentElementKey = Object.keys(formModel).find(
    (key) => formModel[key].formKey === containerFormName
  );
  if (currentElementKey) {
    return formModel[currentElementKey].formLastModified;
  } else {
    return formInfo.formLastModified || '';
  }
};

export const colorBorderHandler = (hasDeltaChanges, hasUiChanges) => {
  const palette = ['5px solid orange'];
  if (hasUiChanges) {
    return palette[0];
  } else {
    return '';
  }
};

export const columnValueToFormula = (entityDefinition, translations, entity) => {
  return `{|${getPropTranslation(entityDefinition, translations, entity)}|${entity}|}`;
};

export const columnTextToFormula = (value) => `"${value}"`;

export const formatControlTypeName = (controlType) => {
  switch (controlType) {
    case SS_CONTROL_TYPE.GRID:
      return 'Grid';
    case SS_CONTROL_TYPE.SINGLE_TAB:
      return 'Tab';
    case SS_CONTROL_TYPE.PANEL:
      return 'Panel';
    case SS_CONTROL_TYPE.BUTTON:
      return 'Button';
    case SS_CONTROL_TYPE.REPEATING_PANEL:
      return 'Repeating Panel';
    default:
      return controlType;
  }
};

export const formatStringForRowTitleRepeatingPanel = (str) => {
  const index = str && String(str).lastIndexOf('(');
  return str && index > -1 ? str.substring(0, index - 1) : str;
};

export const ElementGenerator = ({
  selectedType,
  controlParentId,
  isEntity,
  dataModel,
  defaultLabel,
  commonZoomProps,
  entityName,
  formName,
}) => {
  const controlId = getControlIdForNewElement(selectedType);
  const controlData = !isEntity && dataModel ? dataModel.split('.') : '';
  const commonProperties = {
    controlId,
    parent: controlParentId,
    entityName: controlData && controlData[0] ? controlData[0] : entityName ?? '',
    fieldName: controlData && controlData[1] ? controlData[1] : '',
    defaultLabel: defaultLabel ?? '',
  };

  const zoomProps = {
    controlId,
    parent: controlParentId,
    ...commonZoomProps,
  };

  switch (selectedType) {
    case SS_CONTROL_TYPE.TEXT_BOX:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.TEXT_BOX,
          enforceSelection: true,
        },
      };
    case SS_CONTROL_TYPE.TEXT_VIEW:
      return { [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.TEXT_VIEW } };
    case SS_CONTROL_TYPE.DROP_DOWN:
      return { [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.DROP_DOWN } };
    case SS_CONTROL_TYPE.TEXT_AREA:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.TEXT_AREA,
          supportRichText: false,
        },
      };
    case SS_CONTROL_TYPE.RADIO_BUTTON_LIST:
      return {
        [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.RADIO_BUTTON_LIST },
      };

    case SS_CONTROL_TYPE.PANEL:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          entityName,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.PANEL,
        },
      };

    case SS_CONTROL_TYPE.REPEATING_PANEL:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          entityName,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.REPEATING_PANEL,
        },
      };

    case SS_CONTROL_TYPE.GRID:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          entityName,
          controlType: SS_CONTROL_TYPE.GRID,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.SUB_GRID:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          entityName,
          controlType: SS_CONTROL_TYPE.SUB_GRID,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.GRID_COLUMN:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          entityName,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.GRID_COLUMN,
          defaultLabel: defaultLabel ?? '',
        },
      };
    case SS_CONTROL_TYPE.TAB_SET:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.TAB_SET,
        },
      };
    case SS_CONTROL_TYPE.SINGLE_TAB:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.SINGLE_TAB,
          defaultLabel: defaultLabel ?? '',
        },
      };
    case SS_CONTROL_TYPE.SUB_FORM:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.SUB_FORM,
          formName,
        },
      };
    case SS_CONTROL_TYPE.FORM:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          childrenNodes: [],
          controlType: SS_CONTROL_TYPE.FORM,
        },
      };
    case SS_CONTROL_TYPE.DATE_BOX:
      return { [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.DATE_BOX } };

    case SS_CONTROL_TYPE.NUMERIC_TEXT_BOX:
      return {
        [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.NUMERIC_TEXT_BOX },
      };

    case SS_CONTROL_TYPE.CHECK_BOX:
      return { [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.CHECK_BOX } };

    case SS_CONTROL_TYPE.HYPER_LINK:
      return { [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.HYPER_LINK } };

    case SS_CONTROL_TYPE.STATIC_TEXT:
      return { [controlId]: { ...commonProperties, controlType: SS_CONTROL_TYPE.STATIC_TEXT } };

    case SS_CONTROL_TYPE.BUTTON:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.BUTTON,
        },
      };

    case SS_CONTROL_TYPE.BUTTON_SHARE_POINT:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.BUTTON_SHARE_POINT,
        },
      };

    case SS_CONTROL_TYPE.BUTTON_BATCH:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.BUTTON_BATCH,
        },
      };

    case SS_CONTROL_TYPE.BUTTON_BULK_UPLOAD:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.BUTTON_BULK_UPLOAD,
        },
      };

    case SS_CONTROL_TYPE.ZOOM:
      return {
        [controlId]: {
          ...zoomProps,
          controlType: SS_CONTROL_TYPE.ZOOM,
        },
      };

    case SS_CONTROL_TYPE.REPOSITORY_ZOOM:
      return {
        [controlId]: {
          ...zoomProps,
          controlType: SS_CONTROL_TYPE.REPOSITORY_ZOOM,
        },
      };

    case SS_CONTROL_TYPE.FILE_INPUT:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.FILE_INPUT,
        },
      };

    case SS_CONTROL_TYPE.FILE_VIEWER:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.FILE_VIEWER,
        },
      };

    case SS_CONTROL_TYPE.ADVANCED_TEXT_BOX:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.ADVANCED_TEXT_BOX,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.FIELD_GROUP:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.FIELD_GROUP,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.MODAL:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.MODAL,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.SAVED_SEARCH:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.SAVED_SEARCH,
        },
      };
    case SS_CONTROL_TYPE.BUTTON_SCORM:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.BUTTON_SCORM,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.BUTTON_OFFICE_ONLINE:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.BUTTON_OFFICE_ONLINE,
        },
      };

    case SS_CONTROL_TYPE.BUTTON_INSPECTION_MEASUREMENT_DATA:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.BUTTON_INSPECTION_MEASUREMENT_DATA,
        },
      };

    case SS_CONTROL_TYPE.BUTTON_CONTROL_PRINT:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.BUTTON_CONTROL_PRINT,
        },
      };

    case SS_CONTROL_TYPE.SCORM_FILE_INPUT:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.SCORM_FILE_INPUT,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.REGULATORY_TERMS_ZOOM:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.REGULATORY_TERMS_ZOOM,
        },
      };

    case SS_CONTROL_TYPE.BUTTON_DECISION_TREE:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.BUTTON_DECISION_TREE,
        },
      };
    case SS_CONTROL_TYPE.SLIDER: {
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.SLIDER,
          minValue: '0',
          maxValue: '100',
        },
      };
    }
    case SS_CONTROL_TYPE.ICON:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.ICON,
          fontSize: ICON_DEFAULT_SIZE,
          color: ICON_DEFAULT_COLOR,
        },
      };
    case SS_CONTROL_TYPE.AI_SUMMARY:
      return {
        [controlId]: {
          ...commonProperties,
          controlType: SS_CONTROL_TYPE.AI_SUMMARY,
        },
      };

    case SS_CONTROL_TYPE.CLUSTER_COLUMN_CHART:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.CLUSTER_COLUMN_CHART,
          chartData: [],
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.AI_SIMILAR_RECORDS:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.AI_SIMILAR_RECORDS,
          showOperationCompleteMessage: true,
          childrenNodes: [],
        },
      };

    case SS_CONTROL_TYPE.ROLE_ACTORS_MANAGEMENT:
      return {
        [controlId]: {
          controlId,
          parent: controlParentId,
          controlType: SS_CONTROL_TYPE.ROLE_ACTORS_MANAGEMENT,
          childrenNodes: [],
        },
      };

    default:
      break;
  }
};

const getConditionText = ({ condition, isTranslated }) => {
  if (!condition) {
    return '';
  }

  const leftValue =
    condition.LeftValue.includes('|') && isTranslated
      ? condition.LeftValue.split('|')[1]
      : condition.LeftValue;

  const rightValue =
    condition.RightValue.includes('|') && isTranslated
      ? condition.RightValue.split('|')[1]
      : condition.RightValue;

  return `${condition.OpenGroup ? '(' : ''} ${leftValue} ${condition.SqlOperator &&
    SQL_DICTIONARY[condition.SqlOperator]} ${rightValue} ${condition.CloseGroup ? ')' : ''} ${
    condition.RelationalOperator
  }`;
};

export const tooltipExtraLabelBuilder = (Conditions) => {
  return Conditions && Conditions.length
    ? Conditions.reduce((acc, current) => {
        return [...acc, { subtitle: getConditionText({ condition: current }) }];
      }, [])
    : [{ subtitle: 'True' }];
};

export const getConditionsTextTooltip = (Conditions) => {
  const conditionBuilderText = [];
  if (Conditions && Conditions.length) {
    let description = '';
    description = getConditionText({ condition: Conditions[0] });
    if (!description) {
      description = 'True';
    } else {
      description = description.length > 100 ? `${description.substring(0, 97)}...` : description;
    }
    conditionBuilderText.push({ subtitle: description });
    if (Conditions.length > 1) {
      conditionBuilderText.push({ subtitle: `${Conditions.length - 1} More...` });
    }
  } else {
    conditionBuilderText.push({ subtitle: 'True' });
  }
  return conditionBuilderText;
};

export const getConditionsText = ({ conditions, isTranslated, showFullText }) => {
  let description = '';

  if (conditions) {
    description = conditions.reduce((acc, condition, index) => {
      if (index > 4) {
        return acc;
      }

      return acc + getConditionText({ condition, isTranslated });
    }, '');

    if (!description) {
      description = 'True';
    }
  }

  if (!showFullText && conditions && conditions.length > 4) {
    return `${description} (${conditions.length - 4} More...)`;
  }

  return description;
};

const truncateActionText = (actionText, maxLength = 97) =>
  actionText && actionText.length > maxLength
    ? `${actionText.substring(0, maxLength)} ...`
    : actionText;

const getActionText = (type, current, isTranslated) => {
  const target = isTranslated ? current.dummyTarget : current.targetMapping;

  return `${type} ${target || ''} ${current.dummySource || ''}\n`;
};

const getValidationActionText = (type, current, isTranslated) => {
  const parameterType = isTranslated ? current.dummyParameterType : current.parameterType;
  const parameter = isTranslated ? current.dummyParameter : current.parameter;

  return `${type} ${parameterType} ${parameter}\n`;
};

export const getActionsText = ({ actions, isTranslated, isValidation }) => {
  const actionBuilderText = [];
  actionBuilderText.push(
    ...actions.reduce((acc, current, index) => {
      if (!isValidation && !ALL_SUPPORTED_UPDATE_ACTIONS_TYPES.includes(current.type)) {
        return acc;
      }

      if (index < 4) {
        const type = isTranslated ? current.dummyType : current.type;

        if (isValidation) {
          acc.push(truncateActionText(getValidationActionText(type, current, isTranslated)));
        } else {
          acc.push(truncateActionText(getActionText(type, current, isTranslated)));
        }
      }
      return acc;
    }, [])
  );

  if (actions && actions.length > 4) {
    actionBuilderText.push(`(${actions.length - 4} More...)`);
  }

  return actionBuilderText;
};

export const getEntityDefinitionOptions = (entityDefinition) => {
  const keys = Object.keys(entityDefinition);
  if (entityDefinition[keys]) {
    keys.push(
      ...Object.keys(entityDefinition[keys]).filter(
        (key) => key !== 'columns' && key !== 'properties'
      )
    );
  }
  return keys;
};

const getPreparedDataObjectList = ({ parentTitle, entityDefinition, entityTranslations }) => {
  const dataObjectList = Object.keys(entityDefinition);
  const translatedDataObjectList = dataObjectList.map((dataObjectTitle) => {
    return {
      translatedTitle: entityTranslations[dataObjectTitle.replace(`${parentTitle}-`, '')],
      dataObjectTitle,
    };
  });

  return translatedDataObjectList;
};

export const getTranslatedEntityDefinitionOptions = ({
  entityDefinition,
  entityTranslations,
  parentTitle,
}) => {
  const sortedDataObjectList = getPreparedDataObjectList({
    parentTitle,
    entityDefinition,
    entityTranslations,
  });

  return sortedDataObjectList.reduce((dataObjectList, item) => {
    const dataObject = entityDefinition[item.dataObjectTitle];
    const dataObjectTitle = parentTitle
      ? item.dataObjectTitle.replace(`${parentTitle}-`, '')
      : item.dataObjectTitle;

    const label = entityTranslations[dataObjectTitle] ?? dataObjectTitle;

    const modifiedDataObject = { label, value: item.dataObjectTitle };

    dataObjectList.push(modifiedDataObject);

    const childrenList = getTranslatedEntityDefinitionOptions({
      entityDefinition: _.omit(dataObject, ['columns', 'properties']),
      parentTitle: item.dataObjectTitle,
      entityTranslations,
    });

    return [...dataObjectList, ...childrenList];
  }, []);
};

export const getNodeProperties = (entityDefinition, nodeId, isEntity) => {
  const lastIndex = nodeId.lastIndexOf('-');
  const parentElement = isEntity ? nodeId : nodeId.substr(0, lastIndex);
  const field = nodeId.substr(1 + lastIndex);
  let properties = '';
  const findProperties = (entityDefinitionNode, parentElement) => {
    if (properties) {
      return properties;
    }
    Object.entries(entityDefinitionNode).forEach(([key, node]) => {
      if (!['properties'].includes(key)) {
        if (key === parentElement) {
          properties = isEntity ? node?.properties : node.columns[field]?.properties;
        } else {
          return findProperties(node, parentElement);
        }
      }
    });
  };
  findProperties(entityDefinition, parentElement);
  return { properties, isEntity };
};

export const getEntityDefinitionInfoNode = (entityDefinition, node) => {
  const { label, childNodes, nodeId } = node;
  let isEntity = false;
  if ('Fields'.includes(label)) {
    return '';
  } else if (childNodes) {
    isEntity = true; //'entity';
  }
  return getNodeProperties(entityDefinition, nodeId, isEntity);
};

export const getDatasourceFields = (dataSource) => {
  if (!dataSource?.columnList) {
    return [];
  }

  const datasourceFields = dataSource?.columnList || [];

  return datasourceFields.map((value) => ({
    label: value,
    value,
  }));
};

export const getDataSourceSelectValues = (dataSourceList) =>
  dataSourceList.map(({ dataSourceKey, dataSourceName, dataSourceType }) => ({
    label: dataSourceType === DATASOURCE_TYPE_PREDEFINED ? dataSourceName : dataSourceKey,
    value: dataSourceKey,
  }));

const getControlsWithPrefix = (controls, prefix) =>
  controls.map((control) => `${prefix}--${control}`);

const getControlsExcept = (controls, except) =>
  controls.filter((control) => !except.includes(control));

export const acceptedDropTypes = (parentType, prefix) => {
  if (parentType) {
    const rules = {
      [SS_CONTROL_TYPE.FORM]: [
        `${prefix}--${SS_CONTROL_TYPE.PANEL}`,
        `${prefix}--${SS_CONTROL_TYPE.GRID}`,
      ],
      [SS_CONTROL_TYPE.PANEL]: [
        ...getControlsWithPrefix(
          getControlsExcept(ALL_CONTROLS, [SS_CONTROL_TYPE.GRID_COLUMN, SS_CONTROL_TYPE.SUB_GRID]),
          prefix
        ),
      ],
      [SS_CONTROL_TYPE.GRID]: [
        `${prefix}--${SS_CONTROL_TYPE.GRID_COLUMN}`,
        `${prefix}--${SS_CONTROL_TYPE.SUB_GRID}`,
      ],
      [SS_CONTROL_TYPE.GRID_COLUMN]: [
        ...getControlsWithPrefix(
          getControlsExcept(ALL_CONTROLS, [SS_CONTROL_TYPE.GRID_COLUMN, SS_CONTROL_TYPE.SUB_GRID]),
          prefix
        ),
      ],
      [SS_CONTROL_TYPE.SUB_GRID]: [
        ...getControlsWithPrefix(
          getControlsExcept(ALL_CONTROLS, [SS_CONTROL_TYPE.GRID_COLUMN, SS_CONTROL_TYPE.SUB_GRID]),
          prefix
        ),
      ],
      [SS_CONTROL_TYPE.REPEATING_PANEL]: [
        ...getControlsWithPrefix(
          getControlsExcept(ALL_CONTROLS, [SS_CONTROL_TYPE.GRID_COLUMN, SS_CONTROL_TYPE.SUB_GRID]),
          prefix
        ),
      ],
      [SS_CONTROL_TYPE.TAB_SET]: [`${prefix}--${SS_CONTROL_TYPE.SINGLE_TAB}`],
      [SS_CONTROL_TYPE.SINGLE_TAB]: [
        ...getControlsWithPrefix(
          getControlsExcept(ALL_CONTROLS, [SS_CONTROL_TYPE.GRID_COLUMN, SS_CONTROL_TYPE.SUB_GRID]),
          prefix
        ),
      ],
    };

    return parentType in rules ? rules[parentType] : ['unsupported type'];
  } else {
    return ['unsupported type'];
  }
};

export const getDataSourceObjectList = (dataLookUpType) => {
  if (dataLookUpType === DATASOURCE_TYPE_ENUM[DATASOURCE_TYPE_SMARTSOLVEDATA]) {
    return getEntityCatalog({ entityKey: '' }).then((res) => {
      const { entityCatalog, hasError } = res;
      if (!hasError) {
        if (entityCatalog) {
          const objectList = entityCatalog.Entity.map((object) => ({
            value: object.Label,
            label: object.Label,
          }));
          return objectList;
        }
        return [];
      }
    });
  } else if (dataLookUpType === DATASOURCE_TYPE_ENUM[DATASOURCE_TYPE_EXTERNALDATASOURCE]) {
    return getExternalDatasources().then((res) => {
      const { ExternalDataSources, hasError } = res;
      if (!hasError) {
        if (ExternalDataSources) {
          const objectList = ExternalDataSources.map((object) => ({
            value: object.Source,
            label: object.Description,
          })).sort((a, b) => a?.Description?.localeCompare(b?.Description));
          return objectList;
        }
        return [];
      }
    });
  }
};

export const getDataSourceFieldList = ({
  dataLookupType = '',
  dataObject = '',
  formDataObjectList,
}) => {
  if (
    dataLookupType === DATASOURCE_TYPE_ENUM[DATASOURCE_TYPE_SMARTSOLVEDATA] ||
    dataLookupType === DATASOURCE_TYPE_ENUM[DATASOURCE_TYPE_LISTDATA]
  ) {
    return getDatasourceFieldsApi({
      entityKey: dataObject,
      dataLookupType,
    }).then((res) => {
      const { hasError, fields } = res;
      if (!hasError) {
        if (fields) {
          const fieldList = fields
            .map((field) => ({ ...field, value: field?.Name, label: field?.Label }))
            .sort((a, b) => a?.Label?.localeCompare(b?.Label));
          return fieldList;
        }
      }
      return [];
    });
  }
  if (dataLookupType === DATASOURCE_TYPE_ENUM[DATASOURCE_TYPE_EXTERNALDATASOURCE]) {
    return getExternalDatasources().then((res) => {
      const { ExternalDataSources, hasError } = res;
      if (!hasError) {
        if (ExternalDataSources) {
          const selectedSourceBySourceType = ExternalDataSources.find(
            ({ Source }) => Source === dataObject
          );
          const fieldList = selectedSourceBySourceType?.SelectValueColumns.split(';').map(
            (field) => ({
              value: field,
              label: field,
            })
          );
          return fieldList ?? [];
        }
      }
      return [];
    });
  }
  if (dataLookupType === DATASOURCE_TYPE_ENUM[DATASOURCE_TYPE_FORMDATA] && formDataObjectList) {
    const fieldList = _.find(formDataObjectList, { value: dataObject })?.fields ?? [];
    return Promise.resolve(fieldList);
  }
  return Promise.resolve([]);
};

export const getSelectedSourceName = (row = {}, key, dataSourceSelectValues = []) => {
  if (row.sourceType !== 'datasource') {
    return row[key];
  }
  // row[key] could be dataSource name or dataSource column name
  const isDataSourceName = dataSourceSelectValues.some(({ value }) => value === row[key]);
  return isDataSourceName ? row[key] : row.source;
};

export const getSubFormNamesList = async (formEntityKey, formKey, subFormNamesArr) => {
  const { formsList, hasError } = await getFormListByEntityKey(formEntityKey);

  return hasError
    ? []
    : formsList.filter((formName) => !subFormNamesArr.includes(formName) && formName !== formKey);
};

export const removeChildrenNodes = (formModel = {}) => {
  const formModelKeys = Object.keys(formModel);

  return formModelKeys.reduce((newFormModel, key) => {
    newFormModel[key] = _.omit(formModel[key], 'childrenNodes');

    return newFormModel;
  }, {});
};

export const getStatusText = (status) =>
  status === 2 ? 'Unpublished' : status === 1 ? 'Published' : 'Inworks';

export const modifyConfigurations = (configurations) =>
  configurations.reduce(
    (acc, item) => ({
      ...acc,
      [item.configurationName]: _.pick(item, ['inboundVariables', 'outboundVariables']),
    }),
    {}
  );

export const modifyDataObjectValues = (configurations) => {
  return configurations.map((item) => ({
    value: item.configurationName,
    label: item.configurationName,
  }));
};

export const modifyVariables = (variables) => {
  return variables.map((item) => ({ value: item }));
};

export const getTableTranslation = ({ translations = {}, table = '' }) => {
  const tableName = table === 'V_ENU_NONPERSIS_USER_DATA' ? table : table.split('-')[1];
  return translations.Entity[tableName] ?? '';
};

export const findSingleField = (data = []) => {
  let fields = [];

  const traverseNodes = (nodes) => {
    for (const node of nodes) {
      if (node.label === 'Fields' && node.childNodes?.length) {
        fields = [...fields, ...node.childNodes];
      }
      if (fields.length > 1) {
        return;
      }
      if (node.childNodes?.length) {
        traverseNodes(node.childNodes);
      }
    }
  };

  traverseNodes(data);

  return fields.length === 1 ? fields[0] : null;
};

export const getAddButtonTooltipText = (controlType, translateString) => {
  switch (controlType) {
    case SS_CONTROL_TYPE.GRID:
      return translateString('Add a sub grid/detail row');
    case SS_CONTROL_TYPE.GRID_COLUMN:
      return translateString('Add a Grid Column (ssGridColumn) control');
    case SS_CONTROL_TYPE.SINGLE_TAB:
      return translateString('Add a Single Tab (ssSingleTab) control');
    case SS_CONTROL_TYPE.TEXT_BOX:
      return translateString('Add a control to ssTextBox');
    default:
  }
};

export const getFlattenedEntityFields = (entityDefinition, entityTranslations) => {
  const renderTree = (definition, currentNode) => {
    const keys = Object.keys(definition);

    if (keys.length === 1) {
      const rootNodeId = keys[0];
      const root = {
        nodeId: rootNodeId,
        name: `${entityTranslations[rootNodeId]} (${rootNodeId}) ${entityTranslations[rootNodeId]}`,
        childNodes: [],
        value: rootNodeId,
      };
      return renderTree(definition[rootNodeId], root);
    }

    const fieldsDef = {
      nodeId: `${currentNode.nodeId}-fields`,
      name: 'Fields',
      childNodes: [],
      value: currentNode.nodeId,
    };

    Object.entries(definition.columns)
      .filter(([ckey]) => !ckey.includes('WARNINGS') && !ckey.includes('ERRORS'))
      .forEach(([ckey, cValue]) => {
        const translation = entityTranslations[cValue.properties.Caption];
        fieldsDef.childNodes.push({
          nodeId: `${currentNode.nodeId}-${ckey}`,
          name: `${translation} (${currentNode.nodeId}.${ckey})`,
          value: `{|${translation}|${currentNode.nodeId}.${ckey}|}`,
        });
      });

    currentNode.childNodes.push(fieldsDef);

    keys
      .filter((key) => key !== 'columns' && key !== 'properties')
      .forEach((key) => {
        const childNode = {
          nodeId: key,
          name: `${entityTranslations[key.replace(`${currentNode.nodeId}-`, '')]} (${key})`,
          value: key,
          childNodes: [],
        };
        currentNode.childNodes.push(renderTree(definition[key], childNode));
      });

    return currentNode;
  };

  const flattenTree = (node) =>
    node.childNodes && node.childNodes.length > 0 ? node.childNodes.flatMap(flattenTree) : [node];

  const tree = renderTree(entityDefinition, {});
  return flattenTree(tree);
};
